import React from 'react';
import { PolicyPage, Section, Callout } from './PolicyLayout';

const RELATED = [
  { label: 'Privacy Policy',      path: '/privacy'      },
  { label: 'Terms of Service',    path: '/terms'        },
  { label: 'Cookie Policy',       path: '/cookies'      },
  { label: 'Cancellation Policy', path: '/cancellation' },
];

const SECTIONS = [
  { id: 'agreement',     label: 'Agreement'             },
  { id: 'eligibility',   label: 'Eligibility'           },
  { id: 'bookings',      label: 'Bookings & Payment'    },
  { id: 'conduct',       label: 'Guest Conduct'         },
  { id: 'liability',     label: 'Liability'             },
  { id: 'intellectual',  label: 'Intellectual Property' },
  { id: 'modifications', label: 'Tour Modifications'    },
  { id: 'disputes',      label: 'Disputes'              },
  { id: 'governing',     label: 'Governing Law'         },
  { id: 'contact',       label: 'Contact'               },
];

const TermsOfService = () => (
  <PolicyPage
    badge="Legal · Terms"
    title="Terms of Service"
    subtitle="These terms govern your use of our platform and services. By booking with us, you agree to these terms in full. Please read them carefully — we have written them to be as clear and fair as possible."
    updated="January 15, 2026"
    heroImage="https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1400&q=80"
    sections={SECTIONS}
    relatedPages={RELATED}
    currentPage="/terms"
  >

    <Section id="agreement" label="The Agreement" title="Acceptance of Terms">
      <p className="policy-p">
        These Terms of Service ("Terms") constitute a legally binding agreement between you ("guest", "you", or "your") and Vole Luxe Travel & Safaris ("we", "us", or "our"). By accessing our website, creating an account, or making a booking, you confirm that you have read, understood, and agree to be bound by these Terms.
      </p>
      <Callout>
        <strong>Key principle:</strong> We aim to be a trustworthy partner in your travel experience. These terms are written to protect both parties fairly — not to create hidden obligations.
      </Callout>
      <p className="policy-p">
        We reserve the right to update these Terms at any time. We will notify you of material changes via email or a prominent notice on our website. Your continued use of our services following notice of changes constitutes acceptance of the updated Terms.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="eligibility" label="Who Can Book" title="Eligibility">
      <p className="policy-p">
        To use our services and make a booking, you must:
      </p>
      <ul className="policy-ul">
        <li>Be at least 18 years of age (or the age of majority in your jurisdiction)</li>
        <li>Have the legal capacity to enter into a binding contract</li>
        <li>Provide accurate, complete, and current registration and booking information</li>
        <li>Not be prohibited from receiving our services under applicable laws</li>
      </ul>
      <p className="policy-p">
        When booking on behalf of a group, you confirm that you have the authority to accept these Terms on behalf of all guests, and that you have informed all guests of our terms and policies. You accept personal responsibility for ensuring all members of your group comply with our Guest Conduct policy.
      </p>
      <p className="policy-p">
        Guests under 18 may participate in tours when accompanied by a responsible adult who holds the booking. Children under 10 may be excluded from certain tours at our discretion for safety reasons — this is noted in the tour description where applicable.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="bookings" label="Bookings" title="Bookings & Payment">
      <p className="policy-p">
        All bookings are subject to availability and are confirmed only upon receipt of full payment unless otherwise agreed in writing.
      </p>
      <ul className="policy-ul">
        <li><strong>Confirmation:</strong> You will receive an instant booking confirmation by email within minutes of successful payment. This confirmation constitutes your contract with us.</li>
        <li><strong>Pricing:</strong> All prices are displayed in USD and are inclusive of applicable taxes unless stated otherwise. Prices are subject to change without notice until a booking is confirmed.</li>
        <li><strong>Payment methods:</strong> We accept all major credit and debit cards, and selected digital payment methods. All payments are processed via our PCI-DSS certified payment provider.</li>
        <li><strong>Currency:</strong> If your card is denominated in a currency other than USD, your bank's exchange rate and any foreign transaction fees apply. We are not responsible for these charges.</li>
        <li><strong>Group bookings:</strong> For private group bookings of 5 or more, please contact our concierge team directly for pricing and availability.</li>
      </ul>
      <Callout>
        Your booking confirmation email is your receipt and entry pass for your tour. Please ensure you can access it on the day — either printed or on your device.
      </Callout>
    </Section>

    <div className="section-divider" />

    <Section id="conduct" label="Guest Conduct" title="Responsibilities & Guest Conduct">
      <p className="policy-p">
        We pride ourselves on creating intimate, respectful experiences. All guests are expected to conduct themselves with courtesy toward our guides, fellow guests, and the historical sites we visit.
      </p>
      <p className="policy-p">You agree that you and all members of your party will:</p>
      <ul className="policy-ul">
        <li>Arrive at the designated meeting point at the confirmed start time — late arrivals may not be accommodated</li>
        <li>Follow all instructions given by your guide, particularly in restricted or sensitive areas</li>
        <li>Treat historical sites, artefacts, and private spaces with the utmost respect — no touching unless explicitly invited</li>
        <li>Refrain from behaviour that disrupts the experience for other guests or the local community</li>
        <li>Declare any relevant medical conditions, mobility limitations, or dietary requirements at the time of booking</li>
        <li>Not attend a tour while under the influence of alcohol or controlled substances</li>
      </ul>
      <p className="policy-p">
        We reserve the right to remove any guest from a tour — without refund — whose behaviour is deemed disruptive, disrespectful, or unsafe. This decision rests entirely with the lead guide and is final.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="liability" label="Liability" title="Limitation of Liability">
      <p className="policy-p">
        We take all reasonable precautions to ensure the safety and quality of our tours. However, some inherent risks exist in any travel and cultural experience.
      </p>
      <ul className="policy-ul">
        <li>We are not liable for personal injury, illness, or loss of property unless directly caused by our proven negligence</li>
        <li>We are not liable for circumstances beyond our reasonable control, including extreme weather, civil unrest, government restrictions, or acts of God</li>
        <li>We are not liable for any indirect, incidental, or consequential losses arising from your use of our services</li>
        <li>Our total liability to you in any circumstances shall not exceed the amount paid for the specific booking giving rise to the claim</li>
      </ul>
      <p className="policy-p">
        We strongly recommend that all guests obtain comprehensive travel insurance covering cancellation, medical emergencies, and loss of personal belongings.
      </p>
      <Callout>
        Nothing in these Terms limits our liability for death or personal injury caused by our negligence, or for fraud or fraudulent misrepresentation.
      </Callout>
    </Section>

    <div className="section-divider" />

    <Section id="intellectual" label="IP Rights" title="Intellectual Property">
      <p className="policy-p">
        All content on our platform — including text, imagery, itineraries, tour concepts, guide biographies, maps, and branding — is the intellectual property of Vole Luxe Travel & Safaris or our licensed partners and is protected by copyright.
      </p>
      <ul className="policy-ul">
        <li>You may not reproduce, distribute, or commercially exploit any of our content without prior written consent</li>
        <li>You may share personal photographs taken on our tours on social media, provided they do not defame us or our guides</li>
        <li>By submitting a review or testimonial, you grant us a non-exclusive licence to use your words (with attribution) in our marketing materials</li>
        <li>Our itineraries, route designs, and tour concepts are proprietary — you may not replicate them commercially</li>
      </ul>
    </Section>

    <div className="section-divider" />

    <Section id="modifications" label="Tour Changes" title="Tour Modifications & Force Majeure">
      <p className="policy-p">
        We reserve the right to make changes to tour routes, content, timings, or guides when necessary. We will endeavour to give reasonable advance notice where possible.
      </p>
      <ul className="policy-ul">
        <li><strong>Minor modifications:</strong> Changes to timing, route order, or guide assignment do not entitle you to a refund</li>
        <li><strong>Significant modifications:</strong> Substantial changes (e.g., removal of a key site) will be communicated at least 48 hours in advance; you may rebook or receive a full refund</li>
        <li><strong>Cancellation by us:</strong> If we cancel a tour, you will receive a full refund within 5 business days. We are not liable for consequential losses (e.g., travel arrangements)</li>
        <li><strong>Force majeure:</strong> Tours cancelled due to circumstances beyond our control will be rescheduled or refunded at our discretion</li>
      </ul>
    </Section>

    <div className="section-divider" />

    <Section id="disputes" label="Disputes" title="Dispute Resolution">
      <p className="policy-p">
        We genuinely want every experience to be exceptional. If something falls short, please contact us in the first instance — most issues can be resolved swiftly.
      </p>
      <ul className="policy-ul">
        <li><strong>First step:</strong> Contact support@voleluxe.co.tz within 14 days of your tour. We will respond within 3 business days.</li>
        <li><strong>Formal complaint:</strong> If unresolved, submit a formal written complaint to our registered address. We will acknowledge within 5 days and resolve within 28 days.</li>
        <li><strong>Mediation:</strong> If still unresolved, both parties agree to attempt good-faith mediation before pursuing legal action.</li>
        <li><strong>Limitation period:</strong> Any claim must be brought within 12 months of the tour date.</li>
      </ul>
    </Section>

    <div className="section-divider" />

    <Section id="governing" label="Governing Law" title="Governing Law & Jurisdiction">
      <p className="policy-p">
        These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Vole Luxe Travel & Safaris is registered, without regard to its conflict of law provisions.
      </p>
      <p className="policy-p">
        Any disputes that cannot be resolved through the process outlined above shall be subject to the exclusive jurisdiction of the courts of our registered jurisdiction. Nothing in this clause affects your statutory rights as a consumer under the laws of your own country of residence.
      </p>
    </Section>

    <div className="section-divider" />

    <Section id="contact" label="Questions" title="Questions About These Terms">
      <p className="policy-p">
        If you have questions about these Terms of Service or require clarification on any provision, please contact us before making a booking.
      </p>
      <ul className="policy-ul">
        <li><strong>Email:</strong> legal@voleluxe.co.tz</li>
        <li><strong>Phone:</strong> +1 (800) LUX-TOUR</li>
        <li><strong>Post:</strong> Legal Department, Vole Luxe Travel & Safaris, 124 Heritage Square, Old Town</li>
        <li><strong>Business hours:</strong> Monday – Friday, 09:00 – 18:00 local time</li>
      </ul>
    </Section>

  </PolicyPage>
);

export default TermsOfService;